import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Phone, Clock, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { useSiteData, useSectionDesign } from "../../context/SiteData";
import { useAuth } from "../../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", tour: "", date: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

export const Contact = () => {
  const { content, tours, fmtPrice } = useSiteData();
  const sec = useSectionDesign("contact");
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/.+@.+\..+/.test(form.email) || !form.message.trim()) {
      toast.error("Please fill in your name, a valid email and your message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, tour: form.tour || null, pickup_date: form.date || null, type: "enquiry" });
      toast.success("Enquiry sent — our concierge will reach out shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <Reveal>
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[var(--brand)]" data-testid="contact-eyebrow">
            {content.contact_eyebrow}
          </p>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0b132b]" data-testid="contact-title">
            {sec.text("headline", content.contact_title)}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-md">
            {sec.text("subtext", content.contact_subtitle)}
          </p>

          <div className="mt-10 space-y-5">
            {[
              { icon: Mail, label: "Email", value: content.contact_email, id: "email" },
              { icon: Phone, label: "Phone", value: content.contact_phone, id: "phone", href: `tel:${(content.contact_phone || "").replace(/[^+\d]/g, "")}` },
              {
                icon: MapPin,
                label: "Address",
                value: content.contact_address,
                id: "address",
                href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(content.contact_address || "")}`,
              },
              { icon: Clock, label: "Hours", value: content.contact_hours, id: "hours" },
            ].map((row) => (
              <div key={row.id} className="flex items-center gap-4" data-testid={`contact-info-${row.id}`}>
                <span className="w-12 h-12 rounded-2xl bg-[var(--brand-soft)] flex items-center justify-center shrink-0">
                  <row.icon className="w-5 h-5 text-[var(--brand)]" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wide text-slate-400">{row.label}</span>
                  {row.href ? (
                    <a
                      href={row.href}
                      data-testid="contact-phone-link"
                      className="block text-sm font-bold text-[#0b132b] mt-0.5 hover:text-[var(--brand)] transition-colors"
                    >
                      {row.value}
                    </a>
                  ) : (
                    <span className="block text-sm font-bold text-[#0b132b] mt-0.5">{row.value}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-[2rem] bg-white border border-slate-100 shadow-[0_30px_70px_-30px_rgba(11,19,43,0.25)] p-7 sm:p-9 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                data-testid="contact-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Full name *"
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email address *"
                className={inputCls}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone (optional)"
                className={inputCls}
              />
              <input
                data-testid="contact-date-input"
                type="date"
                value={form.date}
                onChange={set("date")}
                className={inputCls}
              />
            </div>
            <select
              data-testid="contact-tour-select"
              value={form.tour}
              onChange={set("tour")}
              className={`${inputCls} ${form.tour ? "text-[#0b132b]" : "text-slate-400"}`}
            >
              <option value="">Choose a tour (optional)</option>
              {tours.map((t) => (
                <option key={t.id} value={t.name}>
                  {`${t.name} — from ${fmtPrice(t.sale_price || t.price)}`}
                </option>
              ))}
            </select>
            <textarea
              data-testid="contact-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder="Your message *"
              rows={4}
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold py-4 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Enquiry"}
              <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
